import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError, resolveUrl } from "../../lib/api";
import SignatureCanvas from "react-signature-canvas";
import {
  ArrowLeft,
  Camera,
  Plus,
  Trash2,
  Save,
  Check,
  X,
  AlertTriangle,
  Gauge,
  Fuel,
  CheckCircle2,
  Loader2,
  FileText,
  ImagePlus,
  Eraser,
  Download,
  ExternalLink,
  PenLine,
} from "lucide-react";

const DAMAGE_SEVERITY = [
  { v: "minor", label: "Minor", color: "#3b82f6" },
  { v: "moderate", label: "Moderat", color: "#f59e0b" },
  { v: "severe", label: "Sever", color: "#ef4444" },
];

const CHECKLIST_STATUS = [
  { v: "ok", label: "OK", color: "#22c55e" },
  { v: "issue", label: "Probleme", color: "#f59e0b" },
  { v: "missing", label: "Lipsa", color: "#ef4444" },
];

function DamagesEditor({ items, onChange, onUploadPhoto, label, testid }) {
  const update = (idx, key, val) => {
    const copy = [...items];
    copy[idx] = { ...copy[idx], [key]: val };
    onChange(copy);
  };
  const addPhoto = async (idx, file) => {
    const url = await onUploadPhoto(file);
    if (!url) return;
    const copy = [...items];
    copy[idx] = { ...copy[idx], photo_urls: [...(copy[idx].photo_urls || []), url] };
    onChange(copy);
  };
  const removePhoto = (idx, i) => {
    const copy = [...items];
    copy[idx] = { ...copy[idx], photo_urls: (copy[idx].photo_urls || []).filter((_, j) => j !== i) };
    onChange(copy);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { location: "", description: "", severity: "minor", photo_urls: [] }]);

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-lg">{label}</h3>
        <button type="button" onClick={add} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid={`${testid}-add`}>
          <Plus className="w-3 h-3" /> Adauga daune
        </button>
      </div>
      {items.length === 0 ? (
        <div className="text-xs italic" style={{ color: "var(--brand-text-secondary)" }}>Niciun defect inregistrat.</div>
      ) : (
        <div className="space-y-3">
          {items.map((d, idx) => (
            <div key={idx} className="card-surface p-4 space-y-2" data-testid={`${testid}-item-${idx}`}>
              <div className="grid sm:grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="Locatie (ex: bara fata)"
                  value={d.location || ""}
                  onChange={(e) => update(idx, "location", e.target.value)}
                  className="form-input text-sm"
                />
                <div className="flex gap-2">
                  {DAMAGE_SEVERITY.map((s) => (
                    <button
                      key={s.v}
                      type="button"
                      onClick={() => update(idx, "severity", s.v)}
                      className="text-xs px-3 py-1 rounded border transition"
                      style={{
                        borderColor: d.severity === s.v ? s.color : "var(--brand-border)",
                        background: d.severity === s.v ? `${s.color}22` : "transparent",
                        color: d.severity === s.v ? s.color : "var(--brand-text-secondary)",
                      }}
                    >
                      {s.label}
                    </button>
                  ))}
                  <button type="button" onClick={() => remove(idx)} className="ml-auto p-1 hover:bg-white/5 rounded" title="Sterge">
                    <Trash2 className="w-4 h-4" style={{ color: "var(--brand-text-secondary)" }} />
                  </button>
                </div>
              </div>
              <textarea
                placeholder="Descriere detaliata"
                value={d.description || ""}
                onChange={(e) => update(idx, "description", e.target.value)}
                rows={2}
                className="form-input text-sm resize-none w-full"
              />
              <div className="flex items-center gap-2 flex-wrap">
                {(d.photo_urls || []).map((u, i) => (
                  <div key={i} className="relative">
                    <img src={resolveUrl(u)} alt="" className="w-20 h-20 object-cover rounded border" style={{ borderColor: "var(--brand-border)" }} />
                    <button type="button" onClick={() => removePhoto(idx, i)} className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "var(--brand-bg)", borderColor: "var(--brand-border)" }}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <label className="w-20 h-20 rounded border border-dashed flex items-center justify-center cursor-pointer hover:bg-white/5" style={{ borderColor: "var(--brand-border)" }}>
                  <ImagePlus className="w-5 h-5" style={{ color: "var(--brand-text-secondary)" }} />
                  <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && addPhoto(idx, e.target.files[0])} />
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function InspectionForm() {
  const { reservationId, type } = useParams(); // /admin/inspections/:reservationId/:type
  const navigate = useNavigate();
  const isReturn = type === "return";

  const [reservation, setReservation] = useState(null);
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [data, setData] = useState({
    inspector_name: "",
    mileage_km: "",
    fuel_level: 100,
    exterior_damages: [],
    interior_damages: [],
    checklist: [],
    photo_urls: [],
    notes: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [resRes, insps, tpl] = await Promise.all([
        api.get(`/admin/reservations/${reservationId}`),
        api.get(`/admin/inspections/by-reservation/${reservationId}`),
        api.get(`/admin/inspections/checklist-template`),
      ]);
      setReservation(resRes.data);
      const found = (insps.data || []).find((i) => i.type === type);
      setExisting(found || null);
      if (found) {
        setData({
          inspector_name: found.inspector_name || "",
          mileage_km: found.mileage_km ?? "",
          fuel_level: found.fuel_level ?? 100,
          exterior_damages: found.exterior_damages || [],
          interior_damages: found.interior_damages || [],
          checklist: found.checklist?.length ? found.checklist : tpl.data,
          photo_urls: found.photo_urls || [],
          notes: found.notes || "",
        });
      } else {
        setData((d) => ({ ...d, checklist: tpl.data }));
      }
    } catch (e) {
      window.alert(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [reservationId, type]);

  useEffect(() => { load(); }, [load]);

  const uploadPhoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("reservation_id", reservationId);
    setUploadingPhoto(true);
    try {
      const r = await api.post("/admin/inspections/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return r.data.url;
    } catch (e) {
      window.alert(formatApiError(e));
      return null;
    } finally {
      setUploadingPhoto(false);
    }
  };

  const addGeneralPhoto = async (file) => {
    const url = await uploadPhoto(file);
    if (url) setData((d) => ({ ...d, photo_urls: [...d.photo_urls, url] }));
  };

  const removeGeneralPhoto = (i) => setData((d) => ({ ...d, photo_urls: d.photo_urls.filter((_, j) => j !== i) }));

  const updateChecklist = (idx, key, val) => {
    setData((d) => {
      const copy = [...d.checklist];
      copy[idx] = { ...copy[idx], [key]: val };
      return { ...d, checklist: copy };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...data,
        mileage_km: data.mileage_km === "" ? null : Number(data.mileage_km),
        fuel_level: data.fuel_level === "" ? null : Number(data.fuel_level),
      };
      let savedId;
      if (existing) {
        await api.patch(`/admin/inspections/${existing.id}`, payload);
        savedId = existing.id;
      } else {
        const r = await api.post("/admin/inspections", {
          reservation_id: reservationId,
          type,
          ...payload,
        });
        savedId = r?.data?.id;
      }
      // Re-fetch to keep `existing` in sync (so signature pad + PDF buttons appear)
      try {
        const insps = await api.get(`/admin/inspections/by-reservation/${reservationId}`);
        const found = (insps.data || []).find((i) => i.type === type);
        if (found) setExisting(found);
      } catch (_) {}
      window.alert(`Inspectia de ${isReturn ? "returnare" : "predare"} a fost salvata. Statusul rezervarii a fost actualizat.`);
      // Stay on page so user can now sign + download PDF (instead of navigating away)
    } catch (e) {
      window.alert(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // ====== Signature pad (F3.G2) ======
  const sigRef = useRef(null);
  const [sigEmpty, setSigEmpty] = useState(true);
  const [sigUploading, setSigUploading] = useState(false);

  const clearSig = () => {
    sigRef.current?.clear();
    setSigEmpty(true);
  };

  const saveSig = async () => {
    if (!existing?.id) {
      window.alert("Salveaza intai inspectia, apoi semneaza.");
      return;
    }
    const pad = sigRef.current;
    if (!pad || pad.isEmpty()) {
      window.alert("Semnatura este goala. Te rog semneaza in zona indicata.");
      return;
    }
    setSigUploading(true);
    try {
      // Use trimmed canvas for tighter PNG
      const dataUrl = pad.getTrimmedCanvas().toDataURL("image/png");
      const r = await api.post(`/admin/inspections/${existing.id}/signature`, { signature_data_url: dataUrl });
      setExisting((prev) => ({ ...prev, client_signature_url: r.data.client_signature_url, signed_at: r.data.signed_at }));
      clearSig();
      window.alert("Semnatura clientului a fost inregistrata.");
    } catch (e) {
      window.alert(formatApiError(e));
    } finally {
      setSigUploading(false);
    }
  };

  const removeSig = async () => {
    if (!existing?.id) return;
    if (!window.confirm("Stergi semnatura inregistrata?")) return;
    try {
      await api.delete(`/admin/inspections/${existing.id}/signature`);
      setExisting((prev) => ({ ...prev, client_signature_url: null, signed_at: null }));
    } catch (e) {
      window.alert(formatApiError(e));
    }
  };

  const openPdf = () => {
    if (!existing?.id) return;
    const url = resolveUrl(`/api/admin/inspections/${existing.id}/report.pdf`);
    window.open(url, "_blank", "noopener");
  };

  const downloadPdf = async () => {
    if (!existing?.id) return;
    try {
      const r = await api.get(`/admin/inspections/${existing.id}/report.pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `raport-${type}-${existing.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 1000);
    } catch (e) {
      window.alert(formatApiError(e));
    }
  };

  if (loading) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  return (
    <div className="space-y-6" data-testid="inspection-form">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="btn-ghost p-2" data-testid="inspection-back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="font-serif text-2xl">
              {isReturn ? "Inspectie returnare" : "Inspectie predare"}
            </h1>
            <div className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>
              Rezervare #{reservationId?.slice(0, 8)} · {reservation?.car_name || reservation?.guest_name}
            </div>
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="btn-gold-filled inline-flex items-center gap-2"
          data-testid="inspection-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salveaza inspectia
        </button>
      </div>

      {existing && (
        <div className="card-surface p-3 text-xs flex items-center gap-2" style={{ color: "var(--brand-text-secondary)" }}>
          <CheckCircle2 className="w-4 h-4" style={{ color: "#22c55e" }} />
          Aceasta inspectie exista deja (creata {new Date(existing.created_at).toLocaleString("ro-RO")}). Modificarile vor fi salvate.
        </div>
      )}

      {/* Top metrics */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card-surface p-4">
          <label className="label-eyebrow text-[10px] mb-1 inline-flex items-center gap-1">
            <FileText className="w-3 h-3" /> Inspector
          </label>
          <input
            type="text"
            value={data.inspector_name}
            onChange={(e) => setData({ ...data, inspector_name: e.target.value })}
            className="form-input w-full"
            placeholder="Nume inspector"
            data-testid="inspector-name"
          />
        </div>
        <div className="card-surface p-4">
          <label className="label-eyebrow text-[10px] mb-1 inline-flex items-center gap-1">
            <Gauge className="w-3 h-3" /> Kilometraj (km)
          </label>
          <input
            type="number"
            value={data.mileage_km}
            onChange={(e) => setData({ ...data, mileage_km: e.target.value })}
            className="form-input w-full"
            placeholder="ex: 45230"
            data-testid="mileage-km"
          />
        </div>
        <div className="card-surface p-4">
          <label className="label-eyebrow text-[10px] mb-1 inline-flex items-center gap-1">
            <Fuel className="w-3 h-3" /> Nivel combustibil: {data.fuel_level}%
          </label>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={data.fuel_level}
            onChange={(e) => setData({ ...data, fuel_level: Number(e.target.value) })}
            className="w-full"
            data-testid="fuel-level"
          />
        </div>
      </div>

      {/* Damages */}
      <DamagesEditor
        items={data.exterior_damages}
        onChange={(v) => setData({ ...data, exterior_damages: v })}
        onUploadPhoto={uploadPhoto}
        label="Daune exterioare"
        testid="exterior-damages"
      />
      <DamagesEditor
        items={data.interior_damages}
        onChange={(v) => setData({ ...data, interior_damages: v })}
        onUploadPhoto={uploadPhoto}
        label="Daune interioare"
        testid="interior-damages"
      />

      {/* Checklist */}
      <div data-testid="checklist-section">
        <h3 className="font-serif text-lg mb-3">Lista de verificare</h3>
        <div className="card-surface overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {data.checklist.map((c, idx) => (
                <tr key={c.key} className="border-t" style={{ borderColor: "var(--brand-border)" }}>
                  <td className="px-4 py-2 font-medium">{c.label}</td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      {CHECKLIST_STATUS.map((s) => (
                        <button
                          key={s.v}
                          type="button"
                          onClick={() => updateChecklist(idx, "status", s.v)}
                          className="text-xs px-2 py-1 rounded border transition"
                          style={{
                            borderColor: c.status === s.v ? s.color : "var(--brand-border)",
                            background: c.status === s.v ? `${s.color}22` : "transparent",
                            color: c.status === s.v ? s.color : "var(--brand-text-secondary)",
                          }}
                          data-testid={`check-${c.key}-${s.v}`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="text"
                      placeholder="Nota (optional)"
                      value={c.note || ""}
                      onChange={(e) => updateChecklist(idx, "note", e.target.value)}
                      className="form-input text-xs w-full"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* General photos */}
      <div data-testid="general-photos">
        <h3 className="font-serif text-lg mb-3 inline-flex items-center gap-2">
          <Camera className="w-4 h-4" /> Poze generale (360° masina)
        </h3>
        <div className="flex items-center gap-3 flex-wrap">
          {data.photo_urls.map((u, i) => (
            <div key={i} className="relative">
              <img src={resolveUrl(u)} alt="" className="w-32 h-24 object-cover rounded border" style={{ borderColor: "var(--brand-border)" }} />
              <button type="button" onClick={() => removeGeneralPhoto(i)} className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center bg-black/80 border" style={{ borderColor: "var(--brand-border)" }}>
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <label className="w-32 h-24 rounded border border-dashed flex flex-col items-center justify-center cursor-pointer hover:bg-white/5" style={{ borderColor: "var(--brand-border)" }} data-testid="add-general-photo">
            {uploadingPhoto ? <Loader2 className="w-5 h-5 animate-spin" /> : <>
              <ImagePlus className="w-5 h-5 mb-1" style={{ color: "var(--brand-text-secondary)" }} />
              <span className="text-[10px]" style={{ color: "var(--brand-text-secondary)" }}>Adauga poza</span>
            </>}
            <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && addGeneralPhoto(e.target.files[0])} />
          </label>
        </div>
      </div>

      {/* Notes */}
      <div>
        <h3 className="font-serif text-lg mb-3">Notite suplimentare</h3>
        <textarea
          value={data.notes}
          onChange={(e) => setData({ ...data, notes: e.target.value })}
          rows={4}
          className="form-input w-full resize-none"
          placeholder="Orice observatii relevante despre starea masinii..."
          data-testid="inspection-notes"
        />
      </div>

      {/* Signature & PDF (F3.G2) — visible only after inspection has been saved */}
      {existing?.id && (
        <div className="card-surface p-5 sm:p-6" data-testid="inspection-signature-pdf">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <h3 className="font-serif text-lg flex items-center gap-2">
                <PenLine className="w-4 h-4" /> Semnatura client &amp; Raport PDF
              </h3>
              <p className="text-xs mt-1" style={{ color: "var(--brand-text-secondary)" }}>
                Clientul semneaza in zona de mai jos. Apoi descarci raportul PDF (cu semnatura inclusa).
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={openPdf}
                className="btn-ghost inline-flex items-center gap-2 text-sm"
                data-testid="inspection-pdf-open"
              >
                <ExternalLink className="w-4 h-4" /> Deschide PDF
              </button>
              <button
                type="button"
                onClick={downloadPdf}
                className="btn-gold-filled inline-flex items-center gap-2 text-sm"
                data-testid="inspection-pdf-download"
              >
                <Download className="w-4 h-4" /> Descarca PDF
              </button>
            </div>
          </div>

          {existing.client_signature_url ? (
            <div className="space-y-3" data-testid="inspection-signature-existing">
              <div className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>
                Semnat la: <b>{(existing.signed_at || "").slice(0, 19).replace("T", " ")}</b>
              </div>
              <div
                className="inline-block rounded-md border p-2 bg-white"
                style={{ borderColor: "var(--brand-border)" }}
              >
                <img
                  src={resolveUrl(existing.client_signature_url)}
                  alt="Semnatura client"
                  className="h-24 w-auto"
                  data-testid="inspection-signature-image"
                />
              </div>
              <div>
                <button
                  type="button"
                  onClick={removeSig}
                  className="btn-ghost text-sm inline-flex items-center gap-2"
                  data-testid="inspection-signature-remove"
                >
                  <Trash2 className="w-4 h-4" /> Sterge semnatura
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div
                className="rounded-md border bg-white"
                style={{ borderColor: "var(--brand-border)" }}
              >
                <SignatureCanvas
                  ref={sigRef}
                  penColor="#111111"
                  canvasProps={{
                    width: 600,
                    height: 180,
                    className: "w-full",
                    "data-testid": "inspection-signature-canvas",
                  }}
                  onBegin={() => setSigEmpty(false)}
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={clearSig}
                  className="btn-ghost inline-flex items-center gap-2 text-sm"
                  data-testid="inspection-signature-clear"
                >
                  <Eraser className="w-4 h-4" /> Sterge
                </button>
                <button
                  type="button"
                  onClick={saveSig}
                  disabled={sigUploading || sigEmpty}
                  className="btn-gold-filled inline-flex items-center gap-2 text-sm disabled:opacity-50"
                  data-testid="inspection-signature-save"
                >
                  {sigUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Salveaza semnatura
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end pt-4 sticky bottom-0 py-3" style={{ background: "var(--brand-bg)" }}>
        <button
          onClick={save}
          disabled={saving}
          className="btn-gold-filled inline-flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salveaza inspectia
        </button>
      </div>
    </div>
  );
}
